/**
 * Transaction form body for ManufacturingInventoryMaterialDrawer (keeps drawer under size cap).
 */
import {
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterial, MfgPlant, MfgUnit } from "@/lib/manufacturingClient";

export type InventoryTxAction =
  | "receive"
  | "issue"
  | "adjust"
  | "reprice"
  | "return"
  | "scrap"
  | "transfer";

export function ManufacturingInventoryMaterialTxForm({
  mat,
  action,
  qty,
  setQty,
  cost,
  setCost,
  newBal,
  setNewBal,
  note,
  setNote,
  targetUnitId,
  setTargetUnitId,
  toPlantId,
  setToPlantId,
  units,
  plants,
  busy,
  onSubmit,
}: {
  mat: MfgMaterial;
  action: InventoryTxAction;
  qty: string;
  setQty: (v: string) => void;
  cost: string;
  setCost: (v: string) => void;
  newBal: string;
  setNewBal: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  targetUnitId: string;
  setTargetUnitId: (v: string) => void;
  toPlantId: string;
  setToPlantId: (v: string) => void;
  units: readonly MfgUnit[];
  plants: readonly MfgPlant[];
  busy: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-2 rounded-xl border border-gray-200 p-3 dark:border-zinc-700">
      {action === "adjust" ? (
        <label className="block text-xs">
          New on-hand balance ({mat.unit})
          <input
            type="number"
            className={`${inputClass} mt-0.5 w-full`}
            value={newBal}
            onChange={(e) => setNewBal(e.target.value)}
            placeholder={String(mat.on_hand_qty)}
          />
        </label>
      ) : action === "reprice" ? (
        <label className="block text-xs">
          New on-hand price ({mat.unit})
          <input
            type="number"
            className={`${inputClass} mt-0.5 w-full`}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder={String(mat.unit_cost)}
          />
        </label>
      ) : (
        <div className="flex gap-2">
          <label className="flex-1 text-xs">
            Quantity ({mat.unit})
            <input
              type="number"
              className={`${inputClass} mt-0.5 w-full`}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          {action === "receive" ? (
            <label className="flex-1 text-xs">
              Unit cost
              <input
                type="number"
                className={`${inputClass} mt-0.5 w-full`}
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder={String(mat.unit_cost)}
              />
            </label>
          ) : null}
        </div>
      )}

      {action === "issue" ? (
        <label className="block text-xs">
          Issue to unit (optional)
          <select
            className={`${inputClass} mt-0.5 w-full`}
            value={targetUnitId}
            onChange={(e) => setTargetUnitId(e.target.value)}
          >
            <option value="">Unassigned</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_label}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {action === "transfer" ? (
        <label className="block text-xs">
          Destination plant
          <select
            className={`${inputClass} mt-0.5 w-full`}
            value={toPlantId}
            onChange={(e) => setToPlantId(e.target.value)}
          >
            <option value="">Select…</option>
            {plants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_code} · {p.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {action === "receive" ||
      action === "adjust" ||
      action === "reprice" ||
      action === "return" ||
      action === "scrap" ||
      action === "transfer" ? (
        <input
          className={`${inputClass} w-full`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
        />
      ) : null}

      <button
        type="button"
        className={`${mfgPrimaryBtnClass} w-full`}
        disabled={busy}
        onClick={onSubmit}
      >
        {busy ? "Saving…" : "Post"}
      </button>
    </div>
  );
}
